<script setup>
import { computed, defineEmits, defineModel, defineProps, ref, watch } from 'vue';
import SelectDropdown from '@/components/UI/SelectDropdown.vue';
import InputFilter from '@/components/UI/InputFilter.vue';
import TrashCanSvg from '@/components/UI/svg/TrashCanSvg.vue';
import { isEmpty } from '@/service/utils';

const condition = defineModel({ required: true });

defineProps({
    fields: {
        type: Array,
        required: true,
    },
    criteria: {
        type: Array,
        required: true,
    },
    isFieldsLoading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['remove-condition']);

const criteriaToInputType = {
    NUMBER: 'number',
    TEXT: 'text',
};

const inputType = ref('text');

const isFieldError = computed(() => isEmpty(condition.value.field));
const isCriteriaError = computed(() => isEmpty(condition.value.criteria));
const isInputError = computed(() => condition.value?.filter_value?.data === '');
const isInputDisabled = computed(() => {
    return isEmpty(condition.value.criteria)
        || condition.value.criteria.criteria_type === 'IS_TRUE'
        || condition.value.criteria.criteria_type === 'IS_FALSE';
});
const isDisabledLabelActive = computed(() => isEmpty(condition.value.criteria));
const isInputRequired = computed(() => {
    return !(
        condition.value.criteria?.criteria_type === 'IS_TRUE'
        || condition.value.criteria?.criteria_type === 'IS_FALSE'
    );
});

watch(
    () => condition.value.criteria?.value_type,
    (newCriteriaType, oldCriteriaType) => {
        if (!condition.value.filter_value) {
            condition.value.filter_value = { type: '', data: '' };
        }

        if (newCriteriaType !== oldCriteriaType) {
            condition.value.filter_value.data = '';
            condition.value.filter_value.type = newCriteriaType || '';
        }

        inputType.value = criteriaToInputType[newCriteriaType] || 'text';
    },
);
</script>

<template>
    <div class="w-full flex flex-col items-center justify-center bg-gray-100 rounded-lg">
        <div class="w-full flex justify-between px-6 my-4">
            <div class="font-bold">
                Условие
            </div>
        </div>

        <div class="w-[calc(100%-40px)] flex flex-col lg:flex-row gap-4 border-b pb-8 border-black">
            <SelectDropdown
                class="w-full lg:w-1/3"
                v-model="condition.field"
                :values="fields"
                label="Выберите поле"
                :format-function="(item) => item.name"
                :is-error="isFieldError"
                :is-loading="isFieldsLoading"
                is-loading-label="Синхронизация полей..."
                searchable
                search-placeholder="Поиск поля..."
            />
            <SelectDropdown
                class="w-full lg:w-1/3"
                v-model="condition.criteria"
                :values="criteria"
                label="Выберите условие"
                :format-function="(item) => item.criteria_name"
                :is-error="isCriteriaError"
                searchable
                search-placeholder="Поиск условия..."
            />
            <InputFilter
                v-model="condition.filter_value.data"
                class="w-full lg:w-1/3"
                :is-error="isInputError"
                :type="inputType"
                :is-disabled="isInputDisabled"
                :is-disabled-label-active="isDisabledLabelActive"
                :is-required="isInputRequired"
            />
        </div>

        <div class="h-16 w-full flex items-center justify-end px-6">
            <button
                type="button"
                class="flex items-center justify-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-gray-200 transition-all duration-150"
                @click="emit('remove-condition')"
            >
                <TrashCanSvg class="size-4 stroke-2 stroke-gray-600" />
                <span class="font-bold text-xs text-gray-600">Удалить</span>
            </button>
        </div>
    </div>
</template>
